import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

export type ToastVariant = 'success' | 'error' | 'warn' | 'info';

export interface ToastOptions {
  title?: string;
  message: string;
  variant?: ToastVariant;
  durationMs?: number;
}

interface ToastRecord extends Required<Omit<ToastOptions, 'title'>> {
  id: number;
  title?: string;
}

interface ToastContextValue {
  push: (opts: ToastOptions) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warn: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

/**
 * 알림 공급자가 없어도 되는 자리(여러 화면에 끼우는 공용 부품 — 통합 캘린더 등)용. 없으면 null.
 * 앱은 늘 최상단에 <ToastProvider> 가 있다 — 이것은 부품을 따로 그리는 시험·미리보기를 위한 것.
 */
export function useOptionalToast(): ToastContextValue | null {
  return useContext(ToastContext);
}

// Toast pairs subtle tinted backgrounds with saturated borders
// + dark text for dense readability. The full triad is now
// tokenized via --bg-{tone}-subtle / --border-{tone}-saturated /
// --fg-on-{tone}-tint, so dark-mode flips the whole palette in
// lockstep without per-variant overrides.
const VARIANT_STYLES: Record<
  ToastVariant,
  { bg: string; border: string; fg: string; dot: string }
> = {
  success: {
    bg: 'var(--bg-success-subtle)',
    border: 'var(--border-success-saturated)',
    fg: 'var(--fg-on-success-tint)',
    dot: 'var(--robot-operating)',
  },
  error: {
    bg: 'var(--bg-danger-subtle)',
    border: 'var(--border-danger-saturated)',
    fg: 'var(--fg-on-danger-tint)',
    dot: 'var(--fg-danger)',
  },
  warn: {
    bg: 'var(--bg-warning-subtle)',
    border: 'var(--border-warning-saturated)',
    fg: 'var(--fg-on-warning-tint)',
    dot: 'var(--dday-warning)',
  },
  info: {
    bg: 'var(--bg-info-subtle)',
    border: 'var(--border-info-saturated)',
    fg: 'var(--fg-on-info-tint)',
    dot: 'var(--brand)',
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((arr) => arr.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (opts: ToastOptions) => {
      const id = nextId.current++;
      const rec: ToastRecord = {
        id,
        title: opts.title,
        message: opts.message,
        variant: opts.variant ?? 'info',
        durationMs: opts.durationMs ?? (opts.variant === 'error' ? 8000 : 3500),
      };
      setToasts((arr) => [...arr, rec]);
      window.setTimeout(() => dismiss(id), rec.durationMs);
    },
    [dismiss],
  );

  const value: ToastContextValue = {
    push,
    success: (message, title) => push({ variant: 'success', message, title }),
    error: (message, title) => push({ variant: 'error', message, title }),
    warn: (message, title) => push({ variant: 'warn', message, title }),
    info: (message, title) => push({ variant: 'info', message, title }),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof window !== 'undefined' &&
        createPortal(<ToastStack toasts={toasts} onDismiss={dismiss} />, document.body)}
    </ToastContext.Provider>
  );
}

function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: ToastRecord[];
  onDismiss: (id: number) => void;
}) {
  const wrap: CSSProperties = {
    position: 'fixed',
    top: 16,
    right: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    zIndex: 100,
    maxWidth: 360,
  };

  return (
    <div style={wrap} role="region" aria-label="알림">
      {toasts.map((t) => {
        const s = VARIANT_STYLES[t.variant];
        return (
          <div
            key={t.id}
            role="status"
            style={{
              background: s.bg,
              border: `1px solid ${s.border}`,
              color: s.fg,
              borderRadius: 'var(--radius-md)',
              padding: '10px 12px',
              boxShadow: 'var(--shadow-md)',
              display: 'flex',
              gap: 10,
              fontSize: 13,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: s.dot,
                marginTop: 6,
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              {t.title && <div style={{ fontWeight: 600, marginBottom: 2 }}>{t.title}</div>}
              <div style={{ whiteSpace: 'pre-wrap' }}>{t.message}</div>
            </div>
            <button
              onClick={() => onDismiss(t.id)}
              aria-label="닫기"
              style={{
                border: 'none',
                background: 'transparent',
                color: s.fg,
                cursor: 'pointer',
                fontSize: 14,
                padding: 2,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}
